import React from "react";

export default function DeleteConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = "Delete",
  confirmColor = "#c62828",
  isLoading = false,
  error = null,
}: {
  isOpen: boolean;
  onClose: () => void;
  /** May be async — the spinner then runs itself until it settles. */
  onConfirm: () => void | Promise<void>;
  title: string;
  message: string;
  confirmLabel?: string;
  confirmColor?: string;
  isLoading?: boolean;
  error?: string | null;
}) {
  // Driven from here when the caller's handler is async, so a confirm that
  // writes a hundred records shows something without every caller having to
  // thread its own flag through. `isLoading` still forces it on.
  const [pending, setPending] = React.useState(false);
  const busy = isLoading || pending;

  if (!isOpen) return null;

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  const handleConfirm = () => {
    if (busy) return;
    const running = onConfirm();
    if (!running || typeof running.then !== "function") return;
    setPending(true);
    running.finally(() => setPending(false));
  };

  return (
    <div className="smrtdocs-modal-overlay">
      <div className="smrtdocs-modal" style={{ width: "480px" }}>
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: "1rem" }}>{title}</h2>
        </div>
        <div className="smrtdocs-modal-body">
          <p style={{ fontSize: "0.9rem", lineHeight: 1.5, margin: "0 0 16px" }}>
            {message}
          </p>

          {error && (
            <p style={{ fontSize: "0.85rem", color: "#ef9a9a", margin: "0 0 16px", padding: "8px 10px", background: "rgba(229, 57, 53, 0.12)", borderRadius: 4 }}>
              {error}
            </p>
          )}

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <button
              className="btn-cancel"
              onClick={handleClose}
              disabled={isLoading}
              style={{ opacity: busy ? 0.5 : 1, cursor: busy ? "not-allowed" : "pointer" }}
            >
              Cancel
            </button>
            <button
              className="btn-save"
              style={{
                background: confirmColor,
                borderColor: confirmColor,
                opacity: busy ? 0.7 : 1,
                cursor: busy ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
              disabled={busy}
              onClick={handleConfirm}
            >
              {busy && <span className="spinner" />}
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
